import React, { useEffect } from 'react'

import { Select, Input, FormLabel } from '@pb/components'
import { Preamble } from '@hm-group/fabric-components'
import { getLabelFromStorage, usePrevious } from '@pb/utils'
import cn from 'classnames'
import { omit } from 'ramda'
import { useWatch } from 'react-hook-form'
import { parsePhoneNumber, getCountryCallingCode } from 'react-phone-number-input'

import { SelectFieldConfig, TelephoneFieldConfig } from 'App/duck/types'
import removeLeadingZero from 'features/checkout/helpers/removeLeadingZero'
import { PhoneNumber as PhoneNumberType } from 'features/checkout/types'
import { joinPhoneNumber } from 'helpers/phoneNumber'

import COUNTRY_OPTIONS from './CountryOptions'
import styles from './PhoneNumber.scss'
import { getMarketCode } from 'App/duck/selectors'

export interface PrefixSelectProps extends Omit<SelectFieldConfig, 'value'> {
  label?: string
  value?: string
  required?: boolean
  className?: string
  cellPhoneCopyKey: string
}

export interface PhoneNumberProps extends TelephoneFieldConfig {
  trigger(fieldName: string[]): void
  className?: string
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({ trigger, className, ...props }) => {
  const prefixState = useWatch({ name: 'prefix' }) as string
  const prevPrefix = usePrevious(prefixState)
  const countryCode = prefixState && getCountryCallingCode(prefixState)

  useEffect(() => {
    if (prevPrefix !== prefixState) trigger(['cellPhone'])
  }, [prefixState])

  return (
    <div className={cn([styles.phoneNumberInputWrap, className])}>
      <Input
        {...props}
        disabled={!prefixState}
        style={{
          margin: 0,
          paddingLeft: '76px',
          width: '100%',
        }}
        className={styles.FieldWrap}
        disableLabel
        type="tel"
        autoComplete="tel-national"
      />
      {prefixState && (
        <Preamble className={styles.countryCodeDisplay}>{`+${countryCode}`}</Preamble>
      )}
    </div>
  )
}

export const PrefixSelect: React.FC<PrefixSelectProps> = ({
  label,
  cellPhoneCopyKey,
  required,
  className,
  ...props
}) => {
  const omitProps = omit(['hint'], props)
  const prefixLabel = getLabelFromStorage(label || '')
  const cellPhoneLabel = getLabelFromStorage(cellPhoneCopyKey)

  return (
    <>
      <FormLabel required={required} htmlFor="cellPhone">
        {cellPhoneLabel}
      </FormLabel>
      <Select
        {...omitProps}
        disableLabel
        aria-label={prefixLabel}
        placeholder=" "
        options={COUNTRY_OPTIONS}
        className={cn([styles.countrySelect, className])}
      />
    </>
  )
}

export const phoneToForm =
  ({ prefix, cellPhone }: PhoneNumberType) =>
  (_, getState): PhoneNumberType => {
    const currentMarket = getMarketCode(getState())
    const joinedPhoneNumber = joinPhoneNumber({ prefix, cellPhone })
    return {
      prefix: parsePhoneNumber(joinedPhoneNumber)?.country || currentMarket,
      cellPhone,
    }
  }

export const phoneFromForm = ({
  prefix,
  cellPhone,
}: {
  prefix?: string
  cellPhone?: string
}): PhoneNumberType => {
  if (prefix && cellPhone) {
    return {
      prefix: `+${getCountryCallingCode(prefix)}`,
      cellPhone: removeLeadingZero(cellPhone),
    }
  }

  const parsedPhoneNumber = cellPhone && parsePhoneNumber(cellPhone)

  return {
    prefix: parsedPhoneNumber && `+${getCountryCallingCode(parsedPhoneNumber?.country)}`,
    cellPhone: parsedPhoneNumber?.nationalNumber,
  }
}

export default PhoneNumber
